import apiClient from "./axios";
import { ApiResponse } from "../types";
import { MaybeApproval } from "./approvals";

export interface AjoFeeConfig {
  id: string;
  feeKey: string;
  amount: number;
  description: string | null;
  updatedBy: string | null;
  updatedAt: string;
}

export interface AjoAdminGroup {
  id: string;
  name: string;
  description: string | null;
  contributionAmount: number;
  interval: string;
  startDate: string;
  expectedParticipants: number;
  membersCount: number;
  pendingInvitesCount: number;
  walletBalance: number;
  nextPayoutText: string | null;
  dueText: string | null;
  status: string;
  inviteCode: string;
  createdAt: string;
  updatedAt: string;
  creator: {
    id: string;
    name: string;
    email: string | null;
    phoneNumber: string | null;
  } | null;
}

/** A public group's listing, as PayIQ reviews it. */
export interface AjoListingForReview {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  state: string | null;
  status: string;
  listingStatus: 'pending_review' | 'listed' | 'unpublished';
  listingNote: string | null;
  contributionAmount: number;
  interval: string;
  expectedParticipants: number;
  joinMode: 'approval' | 'automatic';
  requirements: {
    minTier?: number;
    minReliability?: number;
    states?: string[];
    emailDomain?: string;
    questions?: string[];
  } | null;
  listedAt: string | null;
  updatedAt: string;
  /** What in its own words held it — links, numbers, promised returns. */
  flags: string[];
  organiser: {
    id: string;
    name: string;
    tier: number;
    groupsCompleted: number;
    cleanGroupsCompleted: number;
    groupsRunning: number;
  };
  reports: Array<{ id: string; reason: string; note: string | null; createdAt: string }>;
}

export interface AjoListingReportRow {
  id: string;
  reason: string;
  note: string | null;
  createdAt: string;
  group: { id: string; name: string; listingStatus: string } | null;
}

export interface AjoRatingRow {
  id: string;
  stars: number;
  comment: string | null;
  createdAt: string;
  hiddenAt: string | null;
  hiddenReason: string | null;
  organiser: string;
  rater: string;
  group: string | null;
}

export interface AjoCoverPoolSummary {
  enabled: boolean;
  balance: number;
  levied: number;
  covered: number;
  recovered: number;
  /** Covered shares members have not paid back yet. */
  owing: number;
  levyPct: number;
}

/** An organiser's request to take a member out of a running group. */
export interface AjoMemberExitRequest {
  id: string;
  groupId: string;
  memberId: string;
  reason: "cannot_continue" | "deceased";
  status: "pending" | "approved" | "rejected";
  /** refund: not yet paid, gets back what they paid in. shared_debt: already
   *  paid, what they still owe is split between organiser and recipients. */
  mode: "refund" | "shared_debt";
  refundDue: number;
  debtTotal: number;
  sharePerParty: number;
  organiserShare: number;
  note: string | null;
  groupName: string | null;
  memberName: string | null;
  organiserName: string | null;
  createdAt: string;
}

export interface AjoReferralStatsEntry {
  creatorId: string;
  creatorName: string | null;
  creatorEmail: string | null;
  acceptedInvites: number;
}

export interface AjoReferralStats {
  totalAjoReferrals: number;
  topCreators: AjoReferralStatsEntry[];
}

export interface AjoWhatsAppStats {
  groupId: string;
  messagesThisMonth: number;
  monthlyCap: number;
}

export interface AjoGroupHealth {
  groupId: string;
  groupName: string;
  score: number;
  grade: "A" | "B" | "C" | "D" | "F";
  factors: {
    paymentTimeliness: number;
    memberRetention: number;
    defaultRate: number;
    completionProgress: number;
  };
  riskFlags: string[];
}

export interface AjoGroupExport {
  group: {
    id: string;
    name: string;
    contributionAmount: number;
    interval: string;
    status: string;
    currentRound: number;
    totalRounds: number;
    startDate: string;
    createdAt: string;
  };
  members: Array<{
    id: string;
    displayName: string;
    type: string;
    status: string;
    payoutPosition: number | null;
    totalContributed: number;
    totalReceived: number;
    joinedAt: string | null;
  }>;
  rounds: Array<{
    roundNumber: number;
    status: string;
    dueDate: string;
    payoutDate: string | null;
    recipientName: string | null;
    contributions: Array<{
      memberName: string;
      amount: number;
      status: string;
      paidAt: string | null;
      lateFeeCharged: number;
    }>;
  }>;
  exportedAt: string;
}

/** One group's money, from its ledger. */
export interface AjoWalletGroup {
  id: string;
  name: string;
  status: string;
  /** In the PayIQ Ajo wallet, or still on an account of its own. */
  pooled: boolean;
  /** What it holds: every movement that has gone through. */
  balance: number;
  /** What it can spend: the balance, less anything on its way out. */
  available: number;
  moneyIn: number;
  moneyOut: number;
  /** Movements sent with no answer yet, either way. */
  inFlight: number;
  movements: number;
  lastMovementAt: string | null;
}

export interface AjoWalletSummary {
  /** Null until an account is set for the wallet. */
  wallet: {
    accountNumber: string;
    accountName: string | null;
    /** Where the account was set. The environment wins over admin. */
    source: 'env' | 'admin';
    /** Null when the bank could not be asked. */
    bankBalance: number | null;
    owedToGroups: number;
    difference: number | null;
  } | null;
  groups: AjoWalletGroup[];
}

export interface AjoLedgerLine {
  id: string;
  kind: string;
  status: 'pending' | 'posted' | 'failed' | 'unknown';
  amount: number;
  groupDelta: number;
  reference: string;
  narration: string | null;
  error: string | null;
  createdAt: string;
  resolvedAt: string | null;
}

export const ajoApi = {
  getFees: async () => {
    const { data } =
      await apiClient.get<ApiResponse<AjoFeeConfig[]>>("/admin/ajo/fees");
    return data.data;
  },

  getGroups: async () => {
    const { data } =
      await apiClient.get<ApiResponse<AjoAdminGroup[]>>("/admin/ajo/groups");
    return data.data;
  },

  updateFee: async (
    feeKey: string,
    payload: { amount: number; description?: string },
  ) => {
    const { data } = await apiClient.patch<ApiResponse<AjoFeeConfig>>(
      `/admin/ajo/fees/${feeKey}`,
      payload,
    );
    return data.data;
  },

  getGroupDetail: async (id: string) => {
    const { data } = await apiClient.get<ApiResponse<AjoAdminGroup>>(
      `/admin/ajo/groups/${id}`,
    );
    return data.data;
  },

  /** Force-payout is fund-moving, so when maker-checker is on the backend
   *  parks it as a pending approval instead of executing. Callers must
   *  branch on `isApprovalRequired(result)` before treating the response
   *  as a completed payout. */
  forcePayout: async (groupId: string, roundId: string) => {
    const { data } = await apiClient.post<
      ApiResponse<MaybeApproval<unknown>>
    >(`/admin/ajo/groups/${groupId}/rounds/${roundId}/force-payout`);
    return data.data;
  },

  getWallet: async () => {
    const { data } = await apiClient.get<ApiResponse<AjoWalletSummary>>("/admin/ajo/wallet");
    return data.data;
  },

  getGroupLedger: async (groupId: string, page = 1) => {
    const { data } = await apiClient.get<
      ApiResponse<{ items: AjoLedgerLine[]; meta: { total: number; page: number; limit: number; totalPages: number } }>
    >(`/admin/ajo/groups/${groupId}/ledger`, { params: { page } });
    return data.data;
  },

  /** Where every group's money goes. Maker-checker may park it. */
  setWalletAccount: async (accountNumber: string, accountId?: string) => {
    const { data } = await apiClient.put<
      ApiResponse<{ accountNumber: string; accountName: string | null; bankBalance: number }>
    >("/admin/ajo/wallet/account", { accountNumber, ...(accountId ? { accountId } : {}) });
    return data.data;
  },

  openWalletAccount: async () => {
    const { data } = await apiClient.post<
      ApiResponse<{ accountNumber: string; accountName: string | null; bankBalance: number }>
    >("/admin/ajo/wallet/open");
    return data.data;
  },

  /** Fund-moving: maker-checker may park it as an approval. */
  moveToWallet: async (groupId: string) => {
    const { data } = await apiClient.post<
      ApiResponse<MaybeApproval<{ status: 'moved' | 'pending'; moved: number; leftBehind: number }>>
    >(`/admin/ajo/groups/${groupId}/move-to-wallet`);
    return data.data;
  },

  getPendingExits: async () => {
    const { data } = await apiClient.get<ApiResponse<AjoMemberExitRequest[]>>(
      "/admin/ajo/exits",
    );
    return data.data;
  },

  /** Maker-checker: may come back as an approval waiting on a second admin. */
  approveExit: async (exitId: string) => {
    const { data } = await apiClient.post<ApiResponse<MaybeApproval<unknown>>>(
      `/admin/ajo/exits/${exitId}/approve`,
    );
    return data.data;
  },

  rejectExit: async (exitId: string, note: string) => {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      `/admin/ajo/exits/${exitId}/reject`,
      { note },
    );
    return data.data;
  },

  getListings: async (status: 'pending_review' | 'listed' | 'unpublished') => {
    const { data } = await apiClient.get<ApiResponse<AjoListingForReview[]>>(
      "/admin/ajo/listings",
      { params: { status } },
    );
    return data.data;
  },

  approveListing: async (groupId: string) => {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      `/admin/ajo/listings/${groupId}/approve`,
    );
    return data.data;
  },

  /** The organiser is told the reason; only PayIQ can relist it after. */
  unpublishListing: async (groupId: string, reason: string) => {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      `/admin/ajo/listings/${groupId}/unpublish`,
      { reason },
    );
    return data.data;
  },

  getListingReports: async () => {
    const { data } = await apiClient.get<ApiResponse<AjoListingReportRow[]>>(
      "/admin/ajo/listing-reports",
    );
    return data.data;
  },

  dismissListingReport: async (reportId: string) => {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      `/admin/ajo/listing-reports/${reportId}/dismiss`,
    );
    return data.data;
  },

  getRatings: async (includeHidden = false) => {
    const { data } = await apiClient.get<ApiResponse<AjoRatingRow[]>>("/admin/ajo/ratings", {
      params: includeHidden ? { hidden: 'true' } : undefined,
    });
    return data.data;
  },

  hideRating: async (ratingId: string, reason: string) => {
    const { data } = await apiClient.post<ApiResponse<unknown>>(`/admin/ajo/ratings/${ratingId}/hide`, { reason });
    return data.data;
  },

  getCoverPool: async () => {
    const { data } = await apiClient.get<ApiResponse<AjoCoverPoolSummary>>("/admin/ajo/cover-pool");
    return data.data;
  },

  getReferralStats: async () => {
    const { data } = await apiClient.get<ApiResponse<AjoReferralStats>>(
      "/admin/ajo/referral-stats",
    );
    return data.data;
  },

  getWhatsAppStats: async (groupId: string) => {
    const { data } = await apiClient.get<ApiResponse<AjoWhatsAppStats>>(
      `/admin/ajo/groups/${groupId}/whatsapp-stats`,
    );
    return data.data;
  },

  getGroupHealth: async (groupId: string) => {
    const { data } = await apiClient.get<ApiResponse<AjoGroupHealth | null>>(
      `/admin/ajo/groups/${groupId}/health`,
    );
    return data.data;
  },

  getGroupsAtRisk: async () => {
    const { data } = await apiClient.get<ApiResponse<AjoGroupHealth[]>>(
      "/admin/ajo/risk/at-risk",
    );
    return data.data;
  },

  exportGroup: async (groupId: string) => {
    const { data } = await apiClient.get<ApiResponse<AjoGroupExport>>(
      `/admin/ajo/groups/${groupId}/export`,
    );
    return data.data;
  },

  triggerRetention: async () => {
    const { data } = await apiClient.post<
      ApiResponse<{ message: string }> & { message?: string }
    >("/admin/ajo/retention/run");
    return data;
  },
};
